import React from 'react';
import { Lookup } from '@codametrix/ui-components';
import { CMXIcon } from '@codametrix/ui-components';
import { Conditional } from '@codametrix/ui-components';
import { commonActions, commonEnums, AmplifyCore, AmplifyReducers, appServiceLines } from '@codametrix/ui-common';
import {
  DEFAULT_ARGS,
  DEFAULT_ARGS_V2  
} from '../invoice-editor/invoice-editor';
import { SelectList } from '@codametrix/ui-components';
import classNames from 'classnames';
import { objectUtils } from '@codametrix/ui-common';

const { DocumentStatus, FeedbackMeta, ProviderType } = commonEnums;
const { conditionalUtils } = AmplifyCore;
const { InvoiceEditor: InvoiceEditorActions } = commonActions;

const documentStatusOptions = [
  {
    value: DocumentStatus.OPEN,
    label: 'Open'
  },
  {
    value: DocumentStatus.RESOLVED,
    label: 'Resolved'
  },
  {
    value: DocumentStatus.INCOMPLETE,
    label: 'Incomplete'
  },
  {
    value: DocumentStatus.NOT_CORRECTED,
    label: 'Not corrected'
  }
];

type ProviderState = {
  providerQueryStatus: string;
};

type ImprovementProviderSearchProps = {
  improvement: AmplifyAPI.DocumentImprovement;
  organization: CMxAPI.Organization;
  processInstance: AmplifyAPI.ProcessInstance;
  providerSearchResults: any[];
  improvementsAction: CMxCommonApp.Capability;
  onProviderAdd: (improvement: AmplifyAPI.ImprovementRequest) => void;
  onProviderSearch: (query: CMxCommonApp.LookupQuery) => void;
  showAmplifyFeedback: (feedback: AmplifyAPI.AmplifyFeedback) => void;
};

type ImprovementProviderProps = {
  improvement: AmplifyAPI.DocumentImprovement;
  provider: AmplifyAPI.ImprovememtProvider;
  improvementsAction: CMxCommonApp.Capability;
  processInstance: AmplifyAPI.ProcessInstance;
  onProviderUpdate: (improvement: AmplifyAPI.ImprovementRequest) => void;
  onProviderRemove: (improvement: AmplifyAPI.ImprovementRequest) => void;
};

type ImprovementProvidersProps = {
  improvement: AmplifyAPI.DocumentImprovement;
  improvementsAction: CMxCommonApp.Capability;
  processInstance: AmplifyAPI.ProcessInstance;
  onProviderUpdate: (improvement: AmplifyAPI.ImprovementRequest) => void;
  onProviderRemove: (improvement: AmplifyAPI.ImprovementRequest) => void;
};

export class ImprovementProviderSearch extends React.Component<ImprovementProviderSearchProps> {
  static displayName = 'ImprovementProviderSearch';
  handleProviderSearch = (codeQuery: CMxCommonApp.CodeQuery) => {
    const context = {
      ...codeQuery.context,
      searchType: InvoiceEditorActions.PERFORMING_PROVIDER_RESULTS,
      processInstance: this.props.processInstance,
      providerType: commonEnums.ProviderType.REFERRING_PROVIDER,
      shouldFilterActive: true,
      shouldFilterOutExternalProviders: true
    };
    this.props.onProviderSearch({
      ...codeQuery,
      context
    });
  };

  handleProviderAdd = (
    payload: Accelerate.LookupSelectionPayload<CMxAPI.Provider>
  ) => {
    const provider = { ...payload.codeInfo, ...payload.context };
    const providerIdentifier = AmplifyReducers.getProviderInfo(provider);
    const proivderName = provider?.displayName;
    const providerQueryStatus = this.props.improvement?.provider_query
      ? DocumentStatus.OPEN
      : null;
    const isExistingProvider = this.props.improvement.providers.some(
      (pr) => pr.provider_number === providerIdentifier.number
    );
    if (isExistingProvider) {
      const feedback = {
        message: `Provider ${proivderName} already exists, Choose a different provider.`,
        meta: FeedbackMeta.WARNING,
        dismissable: true
      };
      this.props.showAmplifyFeedback(feedback);
    }
    if (providerIdentifier.number && !isExistingProvider) {
      const improvementRequest = {
        processInstance: this.props.processInstance,
        documentImprovement: this.props.improvement,
        improvementProvider: {
          provider_number: providerIdentifier.number,
          provider_name: proivderName,
          provider_query_status: providerQueryStatus,
          deleted: false
        }
      };
      this.props.onProviderAdd(improvementRequest);
    }
  };

  render() {
    const { improvementsAction, providerSearchResults } = this.props;
    const isIPCServiceLine =
      appServiceLines.isIPC(this.props.processInstance?.serviceLine) ?? false;
    const isSurgeryServiceLine =
      appServiceLines.isSurgery(this.props.processInstance?.serviceLine) ??
      false;
    
    const lookupArgs =
      isIPCServiceLine || isSurgeryServiceLine
        ? { ...DEFAULT_ARGS_V2 }
        : { ...DEFAULT_ARGS };
    

    return (
      <div className="improvement-provider-search">
        <Conditional {...improvementsAction}>
          <Lookup
            {...lookupArgs}
            autoFocus={true}
            showSearchButton={false}
            onSearchSelection={this.handleProviderAdd as any}
            onSearch={this.handleProviderSearch}
            searchResults={providerSearchResults}
            actionPrefix={''}
            autoSearch={true}
            placeholderText={'Add provider'}
            attachResultsToPortal={true}
            resultsPortalExtraClass="improvements-lookup-results-portal"
          />
        </Conditional>
      </div>
    );
  }
}

export class ImprovementProvider extends React.Component<
  ImprovementProviderProps,
  ProviderState
> {
  static displayName = 'ImprovementProvider';
  constructor(props: ImprovementProviderProps) {
    super(props);
    this.state = {
      providerQueryStatus: this.props.provider.provider_query_status
    };
    this.handleProviderDelete = this.handleProviderDelete.bind(this);
    this.handleProviderUpdate = this.handleProviderUpdate.bind(this);
  }
  componentDidUpdate(prevProps: ImprovementProviderProps): void {
    if (
      prevProps?.provider?.provider_query_status !==
      this.props?.provider?.provider_query_status
    ) {
      this.setState({
        providerQueryStatus: this.props.provider?.provider_query_status
      });
    }
  }

  handleProviderDelete = (ev: React.MouseEvent) => {
    ev.preventDefault();
    const improvementRequest: AmplifyAPI.ImprovementRequest = {
      processInstance: this.props.processInstance,
      documentImprovement: this.props.improvement,
      improvementProvider: {
        provider_guid: this.props.provider.provider_guid
      }
    };
    this.props.onProviderRemove(improvementRequest);
  };

  handleProviderUpdate = (statusObj: CustomEvent) => {
    this.setState({
      providerQueryStatus: statusObj.detail.value
    });
    const improvementRequest: AmplifyAPI.ImprovementRequest = {
      processInstance: this.props.processInstance,
      documentImprovement: this.props.improvement,
      improvementProvider: {
        provider_guid: this.props.provider?.provider_guid,
        provider_query_status: statusObj.detail.value,
        deleted: false
      }
    };
    this.props.onProviderUpdate(improvementRequest);
  };
  render() {
    const { provider, improvement, improvementsAction } = this.props;
    const documentStatusStyle = [
      'provider-status',
      this.state.providerQueryStatus &&
        `provider-status-${this.state.providerQueryStatus?.toLowerCase()}`
    ];

    const providerReasonView = conditionalUtils.capabilityValue(
      improvementsAction?.actionable
    ) ? (
      <>
        <SelectList
          name={`provider-status-${provider.provider_guid}`}
          options={documentStatusOptions}
          value={provider.provider_query_status}
          onChange={this.handleProviderUpdate}
        />
        <label className="provider-status-select-label">
          <CMXIcon
            svgIconName="BiomeChevronDownSVG20"
            svgIconSize="16"
            svgIconClass="select-checvron"
          />
        </label>
      </>
    ) : (
      <span className="provider-reason-label">
        {objectUtils.capitalize(provider.provider_query_status)}
      </span>
    );
    return (
      <div className="improvement-provider" key={provider.provider_guid}>
        <div className="provider-row-left">
          {improvement?.provider_query && (
            <div className={classNames(documentStatusStyle)}>
              {providerReasonView}
            </div>
          )}
          <span className="provider-name">{provider.provider_name}</span>
        </div>
        <div className="provider-row-right">
          <Conditional {...improvementsAction}>
            <div
              className="provider-remove"
              onClick={this.handleProviderDelete}
              data-testid="improvement-provider-remove">
              <CMXIcon
                svgIconName="BiomeCancelCircle"
                svgIconClass="improvement-provider-remove"
                svgIconSize="18"
              />
            </div>
          </Conditional>
        </div>
      </div>
    );
  }
}

export const ImprovementProviders: React.FC<ImprovementProvidersProps> = (
  props: ImprovementProvidersProps
) => {
  const {
    improvement,
    improvementsAction,
    processInstance,
    onProviderUpdate,
    onProviderRemove
  } = props;

  const providers: AmplifyAPI.ImprovememtProvider[] = improvement.providers;
  const providersView = providers?.map((provider, idx) => {
    return (
      <ImprovementProvider
        key={idx}
        provider={provider}
        processInstance={processInstance}
        improvement={improvement}
        improvementsAction={improvementsAction}
        onProviderUpdate={onProviderUpdate}
        onProviderRemove={onProviderRemove}
      />
    );
  });

  return <div className="improvement-providers">{providersView}</div>;
};

ImprovementProviders.displayName = 'ImprovementProviders';
